import { config } from "../Environment";

type FetchFunction = (url: string, options?: RequestInit) => Promise<Response>;

/**
 * Share a workflow with an entire organization
 */
export const shareWithOrganization = async (
  workflowId: string,
  organizationId: string,
  fetchFn: FetchFunction
) => {
  const response = await fetchFn(
    `${config.API_BASE}/api/author/workflow/${encodeURIComponent(workflowId)}/share/organization`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ workflowId, organizationId }),
    }
  );

  if (!response.ok) {
    const error = await response.text();
    throw new Error(error || 'Failed to share workflow with organization');
  }

  return response.json();
};

/**
 * Share a workflow with a specific collection/class
 */
export const shareWithCollection = async (
  workflowId: string,
  collectionId: string,
  fetchFn: FetchFunction
) => {
  const response = await fetchFn(
    `${config.API_BASE}/api/author/workflow/${encodeURIComponent(workflowId)}/share/collection`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ workflowId, collectionId }),
    }
  );

  if (!response.ok) {
    const error = await response.text();
    throw new Error(error || 'Failed to share workflow with collection');
  }

  return response.json();
};

/**
 * Get all organizations and collections that have access to a workflow
 */
export const getWorkflowSharing = async (
  workflowId: string,
  fetchFn: FetchFunction
) => {
  const response = await fetchFn(
    `${config.API_BASE}/api/author/workflow/${workflowId}/sharing`,
    {
      method: 'GET',
      credentials: 'include',
    }
  );

  if (!response.ok) {
    const error = await response.text();
    throw new Error(error || 'Failed to fetch workflow sharing info');
  }

  return response.json();
};

/**
 * Remove organization access from a workflow
 */
export const unshareFromOrganization = async (
  workflowId: string,
  organizationId: string,
  fetchFn: FetchFunction
) => {
  const response = await fetchFn(
    `${config.API_BASE}/api/author/workflow/${encodeURIComponent(workflowId)}/unshare/organization`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ organizationId }),
    }
  );

  if (!response.ok) {
    const error = await response.text();
    throw new Error(error || 'Failed to unshare workflow from organization');
  }

  return response.json();
};

/**
 * Remove collection access from a workflow
 */
export const unshareFromCollection = async (
  workflowId: string,
  collectionId: string,
  fetchFn: FetchFunction
) => {
  const response = await fetchFn(
    `${config.API_BASE}/api/author/workflow/${encodeURIComponent(workflowId)}/unshare/collection`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ collectionId }),
    }
  );

  if (!response.ok) {
    const error = await response.text();
    throw new Error(error || 'Failed to unshare workflow from collection');
  }

  return response.json();
};
